import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Tv, ChevronDown, ChevronUp, Users, FileText, Eye, EyeOff } from 'lucide-react';
import { series, themes } from '../data/universeData';

const SeriesSection = () => {
  const [expandedSeason, setExpandedSeason] = useState(1);
  const [showVIPCharacters, setShowVIPCharacters] = useState(false);
  const [showPilotScenes, setShowPilotScenes] = useState(false);

  return (
    <section 
      id="series" 
      data-testid="series-section"
      className="py-24 px-6 bg-gradient-to-b from-[#0a0e12] to-[#080C10]"
    >
      <div className="max-w-6xl mx-auto">
        {/* Waterfront District */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-20"
          data-testid="waterfront-series"
        >
          {/* Header */}
          <div className="flex items-center gap-3 mb-4">
            <Tv size={24} className="text-[#C9A84C]" />
            <span className="font-['Outfit'] text-[#C9A84C]/80 text-xs tracking-widest uppercase px-3 py-1 border border-[#C9A84C]/30 rounded">
              {series.waterfront.status}
            </span>
          </div>

          <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-[#C9A84C] mb-3">
            {series.waterfront.title}
          </h2>
          
          <p className="font-['Outfit'] text-[#E8E0D0]/70 text-lg italic mb-6 max-w-3xl">
            "{series.waterfront.subtitle}"
          </p>

          <p className="font-['Outfit'] text-[#E8E0D0]/60 mb-10 max-w-3xl leading-relaxed">
            {series.waterfront.logline}
          </p>

          {/* Seasons Accordion */}
          <div className="space-y-4">
            {series.waterfront.seasons.map((season) => (
              <motion.div
                key={season.number}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-[#111820] border border-[#C9A84C]/15 rounded-lg overflow-hidden"
                data-testid={`season-${season.number}`}
              >
                <button
                  onClick={() => setExpandedSeason(expandedSeason === season.number ? null : season.number)}
                  className="w-full p-6 flex items-center justify-between text-left hover:bg-[#1A2535]/30 transition-colors"
                >
                  <div>
                    <span className="font-['Outfit'] text-[#C9A84C]/60 text-xs tracking-widest block mb-1">
                      SEASON {season.number}
                    </span>
                    <h3 className="font-['Cormorant_Garamond'] text-xl sm:text-2xl font-bold text-[#E8E0D0]">
                      {season.title}
                    </h3>
                    <p className="font-['Outfit'] text-[#E8E0D0]/50 text-sm mt-1 italic">
                      {season.tagline}
                    </p>
                  </div>
                  {expandedSeason === season.number ? (
                    <ChevronUp className="text-[#C9A84C]" size={24} />
                  ) : (
                    <ChevronDown className="text-[#C9A84C]/50" size={24} />
                  )}
                </button>

                <AnimatePresence>
                  {expandedSeason === season.number && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="border-t border-[#C9A84C]/10"
                    >
                      <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                        {season.episodes.map((episode) => (
                          <div
                            key={episode.number}
                            className="bg-[#0a0e12] border border-[#C9A84C]/10 rounded-lg p-4"
                          >
                            <span className="font-['Outfit'] text-[#C9A84C]/60 text-xs tracking-widest">
                              EPISODE {String(episode.number).padStart(2, '0')}
                            </span>
                            <h4 className="font-['Cormorant_Garamond'] text-lg font-bold text-[#E8E0D0] mt-1 mb-2">
                              {episode.title}
                            </h4>
                            <p className="font-['Outfit'] text-[#E8E0D0]/60 text-sm leading-relaxed">
                              {episode.teaser}
                            </p>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Themes Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-20"
          data-testid="themes-section"
        >
          <h3 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#C9A84C] mb-6 text-center">
            THE RULES OF THE WATERFRONT
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {themes.map((theme, index) => (
              <motion.div
                key={theme.title}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-[#111820]/50 border border-[#C9A84C]/10 rounded-lg p-5"
              >
                <h4 className="font-['Outfit'] text-[#C9A84C] text-sm tracking-widest font-semibold mb-2">
                  {theme.title}
                </h4>
                <p className="font-['Outfit'] text-[#E8E0D0]/60 text-sm leading-relaxed">
                  {theme.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* VIP Series - LOCKED */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative"
          data-testid="vip-series"
        >
          <div className="bg-gradient-to-br from-[#15101e] to-[#111820] border border-[#9B7DFF]/20 rounded-lg overflow-hidden">
            {/* Header */}
            <div className="p-8 border-b border-[#9B7DFF]/10">
              <div className="flex items-center gap-3 mb-4">
                <Tv size={24} className="text-[#C9A84C]" />
                <span className="font-['Outfit'] text-[#8B1A1A] text-xs tracking-widest uppercase px-3 py-1 border border-[#8B1A1A]/30 rounded flex items-center gap-2">
                  <Lock size={12} />
                  {series.vip.status}
                </span>
              </div>

              <h2 className="font-['Cormorant_Garamond'] text-3xl font-bold text-[#C9A84C] mb-3">
                {series.vip.title}
              </h2>
              
              <p className="font-['Outfit'] text-[#E8E0D0]/70 text-lg italic mb-4">
                "{series.vip.subtitle}"
              </p>

              <p className="font-['Outfit'] text-[#E8E0D0]/60 max-w-3xl mb-6">
                {series.vip.logline}
              </p>

              {/* Bridge Note */}
              <div className="bg-[#0a0e12] border border-[#C9A84C]/10 rounded p-4 mb-6">
                <p className="font-['Outfit'] text-[#C9A84C]/80 text-sm">
                  <span className="text-[#C9A84C]">●</span> {series.vip.note}
                </p>
              </div>

              {/* Core Premise */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-[#0a0e12]/50 border border-[#C9A84C]/5 rounded p-4">
                  <h4 className="font-['Outfit'] text-[#C9A84C]/60 text-xs tracking-widest mb-2">THE SURFACE</h4>
                  <p className="font-['Outfit'] text-[#E8E0D0]/50 text-sm">
                    {series.vip.corePremise}
                  </p>
                </div>
                <div className="bg-[#0a0e12]/50 border border-[#8B1A1A]/10 rounded p-4">
                  <h4 className="font-['Outfit'] text-[#8B1A1A] text-xs tracking-widest mb-2">THE TRUTH</h4>
                  <p className="font-['Outfit'] text-[#E8E0D0]/50 text-sm">
                    {series.vip.hiddenStory}
                  </p>
                </div>
              </div>
            </div>

            {/* Pilot Episode - LOCKED */}
            {series.vip.pilot && (
              <div className="border-t border-[#C9A84C]/10">
                <button
                  onClick={() => setShowPilotScenes(!showPilotScenes)}
                  className="w-full p-4 flex items-center justify-between text-left hover:bg-[#1A2535]/20 transition-colors"
                  data-testid="vip-pilot-toggle"
                >
                  <div className="flex items-center gap-3">
                    <FileText size={18} className="text-[#8B1A1A]" />
                    <div>
                      <span className="font-['Outfit'] text-[#E8E0D0]/70 text-sm font-semibold block">
                        {series.vip.pilot.title}
                      </span>
                      <span className="font-['Outfit'] text-[#E8E0D0]/40 text-xs italic">
                        {series.vip.pilot.scenes.length} scenes — {series.vip.pilot.tagline}
                      </span>
                    </div>
                    <span className="font-['Outfit'] text-[#8B1A1A]/80 text-[10px] tracking-widest uppercase px-2 py-0.5 border border-[#8B1A1A]/20 rounded ml-2">
                      CLASSIFIED
                    </span>
                  </div>
                  {showPilotScenes ? (
                    <EyeOff className="text-[#8B1A1A]/60" size={18} />
                  ) : (
                    <Eye className="text-[#C9A84C]/30" size={18} />
                  )}
                </button>

                <AnimatePresence>
                  {showPilotScenes && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="p-6 pt-2 space-y-3" data-testid="vip-pilot-scenes">
                        {series.vip.pilot.scenes.map((scene) => (
                          <div
                            key={scene.number}
                            className="bg-[#0a0e12] border border-[#8B1A1A]/10 rounded-lg p-4 relative overflow-hidden group"
                          >
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#8B1A1A]/[0.02] to-transparent pointer-events-none" />
                            <div className="flex items-start gap-4">
                              <span className="font-['Outfit'] text-[#8B1A1A]/60 text-xs tracking-widest mt-1 shrink-0">
                                SC.{String(scene.number).padStart(2, '0')}
                              </span>
                              <div className="flex-1 min-w-0">
                                <h5 className="font-['Cormorant_Garamond'] text-[#E8E0D0] font-bold text-base mb-1">
                                  {scene.title}
                                </h5>
                                <p className="font-['Outfit'] text-[#C9A84C]/40 text-[11px] tracking-wider mb-2">
                                  {scene.location}
                                </p>
                                <p className="font-['Outfit'] text-[#E8E0D0]/40 text-sm leading-relaxed">
                                  {scene.teaser}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                        <div className="text-center py-3">
                          <p className="font-['Outfit'] text-[#8B1A1A]/50 text-xs tracking-widest uppercase">
                            Full screenplay under development — Access restricted
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {/* Characters Preview - Expandable */}
            <div className="border-t border-[#C9A84C]/10">
              <button
                onClick={() => setShowVIPCharacters(!showVIPCharacters)}
                className="w-full p-4 flex items-center justify-between text-left hover:bg-[#1A2535]/20 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Users size={18} className="text-[#C9A84C]/60" />
                  <span className="font-['Outfit'] text-[#E8E0D0]/60 text-sm">
                    {series.vip.characters.length} CHARACTERS — PREVIEW
                  </span>
                </div>
                {showVIPCharacters ? (
                  <ChevronUp className="text-[#C9A84C]/50" size={20} />
                ) : (
                  <ChevronDown className="text-[#C9A84C]/50" size={20} />
                )}
              </button>

              <AnimatePresence>
                {showVIPCharacters && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="p-6 pt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {series.vip.characters.map((char) => (
                        <div 
                          key={char.name}
                          className="bg-[#0a0e12] border border-[#C9A84C]/5 rounded p-3"
                        >
                          <h5 className="font-['Cormorant_Garamond'] text-[#C9A84C] font-bold text-sm">
                            {char.name}
                          </h5>
                          <p className="font-['Outfit'] text-[#E8E0D0]/40 text-xs mb-1">
                            {char.role}
                          </p>
                          <p className="font-['Outfit'] text-[#E8E0D0]/30 text-xs italic">
                            {char.name === "Nathan Hayes" ? (
                              <span className="text-[#8B1A1A]">{char.truth}</span>
                            ) : (
                              char.truth
                            )}
                          </p>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default SeriesSection;
